import * as React from 'react';
import { useNavigate, useParams, Link, Outlet, Params } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import hex from 'crypto-js/enc-hex';
import md5 from 'crypto-js/md5';

import {
  PortalCoreDataService,
  useCoreDataService,
} from '../services/portal-core-data/portal-core-data.service';
import {
  CourseList,
  CourseUri,
} from '../services/portal-core-data/portal-core-data.model';
import { AlertService } from '../services/alert.service';
import { ErrorService, useErrorService } from '../services/error.service';
import { withHooks, clx, formatDate, compare } from '../shared/utils';
import { LoadingComponent } from '../loading/loading.component';

type InstructorCoursesProps = {
  coreData: PortalCoreDataService;
  alertService: AlertService;
  errors: ErrorService;
  navigate: Function;
  selectedCourse: string | undefined;
};
type InstructorCoursesState = {
  loading: boolean;
  courseList: CourseList;
};

class InstructorCourses extends React.Component<
  InstructorCoursesProps,
  InstructorCoursesState
> {
  state: InstructorCoursesState = {
    loading: true,
    courseList: { courses: [] },
  };

  componentDidMount() {
    this.props.coreData.loadCourses().then(
      (courses) => {
        if (courses && courses.courses && courses.courses.length > 0) {
          const courseList = courses;
          courseList.courses!.sort((c1, c2) => {
            return compare(c1.shortLabel!, c2.shortLabel!);
          });
          this.setState({
            loading: false,
            courseList,
          });
        } else {
          this.setState({
            loading: false,
            courseList: { courses: [] },
          });
          this.props.alertService.addAlertToList({
            level: 'warning',
            message:
              'No active courses with enrolled students exist. If you believe this is an error, please notify your account manager.',
            dismissible: true,
          });
        }
      },
      (er) => {
        this.setState({
          loading: false,
          courseList: { courses: [] },
        });
        this.props.errors.handleError(er);
      }
    );
  }

  selectCourse(courseUri: CourseUri): void {
    try {
      this.props.navigate(`/instructor-courses/${courseUri}`);
    } catch (er) {
      this.props.errors.handleError(er);
    }
  }

  isSelected(courseUri: CourseUri): boolean {
    return this.props.selectedCourse === courseUri;
  }

  hashOf(courseUri: CourseUri): string {
    return hex.stringify(md5(courseUri));
  }

  render() {
    const { loading, courseList } = this.state;

    return (
      <>
        <div className="row">
          <div className="col-auto">
            <p>
              <Link
                to="/"
                aria-label="go to the main page"
                data-cy="home"
                id="instructor-courses-back-to-home"
              >
                Home
              </Link>{' '}
              &gt; Request a Report
            </p>
          </div>
        </div>

        <div className="row">
          <div className="col-auto">
            <h2 className="h3 mb-0 mt-0">A List of Active MindTap Courses</h2>
            <p className="subdued small">
              Below is a list of active MindTap courses for which you can
              request a user interaction log.
            </p>
          </div>
        </div>

        {loading ? (
          <LoadingComponent />
        ) : (
          <>
            <div className="row row-cols-1">
              <div className="col mb-2">
                <div className="card d-flex flex-column h-100 p-3 shadow stat">
                  <div className="card-body d-flex flex-column">
                    <div className="card-text">
                      <table className="table table-hover course-listing">
                        <thead>
                          <tr>
                            <th scope="col">
                              <FontAwesomeIcon icon={faCheck} />
                            </th>
                            <th scope="col">Course Name</th>
                            <th scope="col">Course Description</th>
                            <th scope="col">Start Date</th>
                            <th scope="col">End Date</th>
                            <th scope="col">Enrolled Students</th>
                          </tr>
                        </thead>
                        <tbody>
                          {courseList.courses!.map((listing) => (
                            <tr
                              key={this.hashOf(listing.courseUri!)}
                              id={`id-${this.hashOf(listing.courseUri!)}`}
                              onClick={() =>
                                this.selectCourse(listing.courseUri!)
                              }
                              aria-selected="true"
                              className={clx({
                                'selected-course': this.isSelected(
                                  listing.courseUri!
                                ),
                              })}
                            >
                              <td>
                                {this.isSelected(listing.courseUri!) && (
                                  <FontAwesomeIcon
                                    className="text-success"
                                    icon={faCheck}
                                  />
                                )}
                              </td>
                              <td>{listing.shortLabel}</td>
                              <td>{listing.longLabel}</td>
                              <td>{formatDate(listing.startDate)}</td>
                              <td>{formatDate(listing.endDate)}</td>
                              <td>{listing.aggregatedEnrollmentCount}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Outlet />
          </>
        )}
      </>
    );
  }
}

export const InstructorCoursesComponent = withHooks<InstructorCoursesProps, {}>(
  [useErrorService, 'errors'],
  [useCoreDataService, 'coreData'],
  [useNavigate, 'navigate'],
  [
    useParams,
    (params) => ({
      selectedCourse: (params as Params).courseUri,
    }),
  ]
)(InstructorCourses);
